import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useResolvedRef } from '../../hooks/assistant/useResolvedRef';
import { parseAnswerMarkdown, type InlineSegment } from '../../utils/assistantMarkdown';
import type { AnswerSegment, AssistantRef } from '../../utils/assistantReferences';

interface RefProps {
    assistantRef: AssistantRef;
    onNavigate?: () => void;
    /** Name only, never a link (e.g. inside a button). */
    plain?: boolean;
}

/** A `{kind:key}` reference as its live name, linked when it has a page. */
export const AssistantRefLink: React.FC<RefProps> = ({ assistantRef, onNavigate, plain }) => {
    const state = useResolvedRef(assistantRef);
    if (state.status === 'loading') return <span className="assistant-ref assistant-ref--loading">…</span>;
    if (!state.value) return <span className="assistant-ref assistant-ref--unknown">(okänd)</span>;
    if (!state.value.href || plain) return <span className="assistant-ref">{state.value.label}</span>;
    return (
        <Link to={state.value.href} className="assistant-ref" onClick={onNavigate}>
            {state.value.label}
        </Link>
    );
};

const Segments: React.FC<{ segments: InlineSegment[]; onNavigate?: () => void }> = ({ segments, onNavigate }) => (
    <>
        {segments.map((segment, i) => {
            if (segment.type === 'bold') {
                return (
                    <strong key={i}>
                        <Segments segments={segment.children as AnswerSegment[]} onNavigate={onNavigate} />
                    </strong>
                );
            }
            if (segment.type === 'ref') return <AssistantRefLink key={i} assistantRef={segment.ref} onNavigate={onNavigate} />;
            return <React.Fragment key={i}>{segment.text}</React.Fragment>;
        })}
    </>
);

interface Props {
    text: string;
    onNavigate?: () => void;
}

/** An assistant answer: the Markdown subset from assistantMarkdown.ts with live references. */
const AssistantAnswer: React.FC<Props> = ({ text, onNavigate }) => {
    const blocks = useMemo(() => parseAnswerMarkdown(text), [text]);
    return (
        <div className="assistant-answer">
            {blocks.map((block, i) => {
                if (block.type === 'heading') {
                    return (
                        <h6 key={i}>
                            <Segments segments={block.inline} onNavigate={onNavigate} />
                        </h6>
                    );
                }
                if (block.type === 'list') {
                    const items = block.items.map((item, j) => (
                        <li key={j}>
                            <Segments segments={item} onNavigate={onNavigate} />
                        </li>
                    ));
                    return block.ordered ? <ol key={i}>{items}</ol> : <ul key={i}>{items}</ul>;
                }
                return (
                    <p key={i}>
                        {block.lines.map((line, j) => (
                            <React.Fragment key={j}>
                                {j > 0 && <br />}
                                <Segments segments={line} onNavigate={onNavigate} />
                            </React.Fragment>
                        ))}
                    </p>
                );
            })}
        </div>
    );
};

export default AssistantAnswer;
